import React, { useState, useEffect } from 'react';
import { useFloorPlanScale } from '@/hooks/useFloorPlanScale';
import { ScaleData } from '@/types/floorPlanViewer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { calculateDistance } from '@/utils/scaleCalculations';
import { Ruler, Settings, Check, X, Info } from 'lucide-react';

interface ScaleConfigurationProps {
  floorPlanId: string;
  onStartMeasurement?: (callback: (pixels: number) => void) => void;
  onStopMeasurement?: () => void;
  isMeasuring?: boolean;
  onScaleUpdate?: () => void;
}

export function ScaleConfiguration({
  floorPlanId,
  onStartMeasurement,
  onStopMeasurement,
  isMeasuring = false,
  onScaleUpdate,
}: ScaleConfigurationProps) {
  const { scaleData, hasScale, updateScale, formatMeasurement, isLoading } =
    useFloorPlanScale(floorPlanId);

  // State for calibration
  const [calibrationMode, setCalibrationMode] = useState(false);
  const [calibrationPixels, setCalibrationPixels] = useState<number | null>(null);
  const [calibrationActualValue, setCalibrationActualValue] = useState('');
  const [calibrationFeet, setCalibrationFeet] = useState('');
  const [calibrationInches, setCalibrationInches] = useState('');
  const [selectedUnit, setSelectedUnit] = useState<'ft' | 'm' | 'in'>(scaleData?.unit || 'ft');

  // Update selected unit when scale data changes
  useEffect(() => {
    if (scaleData?.unit) {
      setSelectedUnit(scaleData.unit as 'ft' | 'm' | 'in');
    }
  }, [scaleData]);

  // Calculate actual value from feet and inches if using ft unit
  const getActualValue = () => {
    if (selectedUnit === 'ft' && (calibrationFeet || calibrationInches)) {
      const feet = parseFloat(calibrationFeet) || 0;
      const inches = parseFloat(calibrationInches) || 0;
      return feet + inches / 12;
    }
    return parseFloat(calibrationActualValue);
  };

  // Handle starting calibration
  const handleStartCalibration = () => {
    const actualValue = getActualValue();
    if (!actualValue || isNaN(actualValue)) {
      toast.error('Please enter a known distance first');
      return;
    }

    setCalibrationMode(true);
    onStartMeasurement?.((pixels: number) => {
      setCalibrationPixels(pixels);
      onStopMeasurement?.();
    });
  };

  // Apply calibration
  const handleApplyCalibration = async () => {
    if (!calibrationPixels) return;

    const actualValue = getActualValue();
    if (isNaN(actualValue) || actualValue <= 0) {
      toast.error('Please enter a valid positive number');
      return;
    }

    const newPixelsPerUnit = calibrationPixels / actualValue;
    const newScaleData: ScaleData = {
      unit: selectedUnit,
      pixelsPerUnit: newPixelsPerUnit,
      drawingWidth: 0, // These will be calculated by the backend
      drawingHeight: 0,
      originalUnit: selectedUnit.charAt(0).toUpperCase() + selectedUnit.slice(1),
      scale: '1:Custom',
    };

    const success = await updateScale(newScaleData);
    if (success) {
      toast.success(`Scale calibrated: ${newPixelsPerUnit.toFixed(2)} pixels/${selectedUnit}`);
      setCalibrationMode(false);
      setCalibrationPixels(null);
      setCalibrationActualValue('');
      setCalibrationFeet('');
      setCalibrationInches('');
      // Refetch scale data to update hasScale in parent
      onScaleUpdate?.();
    } else {
      toast.error('Failed to update scale');
    }
  };

  // Cancel calibration
  const handleCancelCalibration = () => {
    setCalibrationMode(false);
    setCalibrationPixels(null);
    setCalibrationActualValue('');
    setCalibrationFeet('');
    setCalibrationInches('');
    onStopMeasurement?.();
  };

  // Clear scale
  const handleClearScale = async () => {
    const success = await updateScale(null as any);
    if (success) {
      toast.success('Scale data cleared');
      // Refetch scale data to update hasScale in parent
      onScaleUpdate?.();
    }
  };

  // Test measurement
  const testDistance = calculateDistance({ x: 0, y: 0 }, { x: 100, y: 0 });
  const formattedTestDistance = formatMeasurement(testDistance);

  return (
    <div className="space-y-4">
      {/* Scale Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Ruler className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium">Scale Status</Label>
        </div>
        <div className="flex items-center gap-2">
          {hasScale ? (
            <>
              <Check className="h-4 w-4 text-green-500" />
              <span className="text-sm text-green-600">Active</span>
            </>
          ) : (
            <>
              <X className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Not Set</span>
            </>
          )}
        </div>
      </div>

      {/* Current Scale Info */}
      {hasScale && scaleData && (
        <div className="bg-muted/50 p-3 rounded-lg space-y-2">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Unit:</span>
              <span className="ml-2 font-medium">{scaleData.unit}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Scale:</span>
              <span className="ml-2 font-medium">{scaleData.scale || 'Custom'}</span>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Pixels per unit:</span>
              <span className="ml-2 font-medium">{scaleData.pixelsPerUnit.toFixed(2)}</span>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Test: 100 pixels = <strong className="text-foreground">{formattedTestDistance}</strong>
          </div>
        </div>
      )}

      <Separator />

      {/* Scale Calibration */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium">Scale Calibration</Label>
        </div>

        {!hasScale && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Set up scale to enable accurate measurements on your floor plan
            </AlertDescription>
          </Alert>
        )}

        {/* Unit Selection */}
        <div className="space-y-2">
          <Label htmlFor="unit" className="text-sm">
            Measurement Unit
          </Label>
          <Select
            value={selectedUnit}
            onValueChange={value => {
              setSelectedUnit(value as any);
              // Clear inputs when changing units
              setCalibrationActualValue('');
              setCalibrationFeet('');
              setCalibrationInches('');
            }}
          >
            <SelectTrigger id="unit">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ft">Feet (ft)</SelectItem>
              <SelectItem value="m">Meters (m)</SelectItem>
              <SelectItem value="in">Inches (in)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Known Distance Input */}
        <div className="space-y-2">
          <Label htmlFor="known-distance" className="text-sm">
            Known Distance
          </Label>
          {selectedUnit === 'ft' ? (
            <div className="space-y-2">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    id="known-distance-feet"
                    type="number"
                    placeholder="Feet"
                    value={calibrationFeet}
                    onChange={e => setCalibrationFeet(e.target.value)}
                    onKeyDown={e => e.stopPropagation()}
                    onKeyUp={e => e.stopPropagation()}
                    disabled={calibrationMode}
                  />
                  <span className="text-xs text-muted-foreground mt-1 block">ft</span>
                </div>
                <div className="flex-1">
                  <Input
                    id="known-distance-inches"
                    type="number"
                    placeholder="Inches"
                    value={calibrationInches}
                    onChange={e => setCalibrationInches(e.target.value)}
                    onKeyDown={e => e.stopPropagation()}
                    onKeyUp={e => e.stopPropagation()}
                    disabled={calibrationMode}
                    min="0"
                    max="11.99"
                  />
                  <span className="text-xs text-muted-foreground mt-1 block">in</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Enter feet and inches (e.g., 5'-8" as 5 ft and 8 in)
              </p>
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <Input
                  id="known-distance"
                  type="number"
                  placeholder="e.g., 10"
                  value={calibrationActualValue}
                  onChange={e => setCalibrationActualValue(e.target.value)}
                  onKeyDown={e => e.stopPropagation()}
                  onKeyUp={e => e.stopPropagation()}
                  disabled={calibrationMode}
                  className="flex-1"
                />
                <span className="self-center text-sm text-muted-foreground min-w-[2rem]">
                  {selectedUnit}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Enter a known distance from your CAD drawing
              </p>
            </>
          )}
        </div>

        {/* Calibration Result */}
        {calibrationPixels !== null && getActualValue() > 0 && (
          <div className="bg-blue-50 dark:bg-blue-950/50 p-3 rounded-lg space-y-2">
            <div className="text-sm space-y-1">
              <p>
                Measured: <strong>{calibrationPixels.toFixed(0)} pixels</strong>
              </p>
              <p>
                Scale:{' '}
                <strong>
                  {(calibrationPixels / getActualValue()).toFixed(2)} pixels/{selectedUnit}
                </strong>
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleApplyCalibration} size="sm" className="flex-1">
                <Check className="h-4 w-4 mr-1" />
                Apply
              </Button>
              <Button
                onClick={handleCancelCalibration}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                <X className="h-4 w-4 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Calibration Actions */}
        {!calibrationMode && calibrationPixels === null && (
          <Button
            onClick={handleStartCalibration}
            variant="outline"
            size="sm"
            className="w-full"
            disabled={
              selectedUnit === 'ft'
                ? !calibrationFeet && !calibrationInches
                : !calibrationActualValue
            }
          >
            <Ruler className="h-4 w-4 mr-2" />
            Measure Known Distance
          </Button>
        )}

        {/* Clear Scale */}
        {hasScale && (
          <Button
            onClick={handleClearScale}
            variant="ghost"
            size="sm"
            className="w-full text-destructive hover:text-destructive"
          >
            Clear Scale Data
          </Button>
        )}
      </div>

      {/* Help Text */}
      <div className="text-xs text-muted-foreground space-y-1 pt-2">
        <p className="font-medium">How to calibrate:</p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li>Enter a known distance from your CAD file</li>
          <li>Click "Measure Known Distance"</li>
          <li>Click and drag on the floor plan to measure the same distance</li>
          <li>Apply the calculated scale</li>
        </ol>
      </div>
    </div>
  );
}
